import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  AtSign,
  FileText,
  Image as ImageIcon,
  Mic,
  StickyNote,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "@/stores/hook";
import { setCurrentLeadId } from "@/features/leads/stores/leadFunctionalSlice";
import { invalidateLeadDetail } from "@/lib/queryClient";
import { requestOpenChat } from "@/features/chat/utils/chatOpenBus";
import {
  clearChatPopupAlerts,
  dismissChatPopupAlert,
  useChatPopupAlert,
} from "./chatPopupAlert";

/**
 * The interrupting half of internal chat: "@you", "a file landed on your
 * lead" and "a note was added on your lead". Mounted once in AppLayout so it
 * fires from any page — the whole point is that you do NOT have to be in the
 * chat (or on the leads page) to hear about it.
 *
 * Chat click-through goes through `chatOpenBus`, the same channel the message
 * toast uses, so it lands ON the message (right channel, scrolled, flashed)
 * rather than merely opening the thread. A NOTE click-through opens the lead
 * itself (leads page + drawer), exactly like the re-enquiry popup.
 */
export default function ChatPopupAlertHost() {
  const { current, waiting } = useChatPopupAlert();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  if (!current) return null;

  const isMention = current.kind === "mention";
  const isNote = current.kind === "note";
  const MediaIcon =
    current.mediaKind === "image"
      ? ImageIcon
      : current.mediaKind === "audio"
        ? Mic
        : FileText;

  const openChat = () => {
    // Everything queued behind this one is in the same inbox and, usually,
    // the same thread we're about to open — keeping them would pop a second
    // dialog straight over the chat the user just asked for.
    clearChatPopupAlerts();
    // Let the dialog's close animation eat the click tail before the chat
    // panel mounts (same 16ms trick as RecaptureAlertHost).
    window.setTimeout(() => {
      requestOpenChat({
        leadId: current.leadId,
        leadName: current.leadName ?? undefined,
        messageId: current.messageId,
        childId: current.childId ?? null,
      });
    }, 16);
  };

  const openLead = () => {
    // Same reasoning as openChat: whatever is queued behind this one would pop
    // a second dialog straight over the lead the user just asked for.
    clearChatPopupAlerts();
    const leadId = current.leadId;
    window.setTimeout(() => {
      // The drawer may already be open ON this lead — a same-id dispatch is a
      // no-op there, so bust its detail so the new note is in the list when
      // it (re)renders. A click is a navigation, not a socket refetch.
      invalidateLeadDetail(leadId);
      dispatch(setCurrentLeadId(leadId));
      if (!location.pathname.startsWith("/leads")) navigate("/leads");
    }, 16);
  };

  const title = isMention
    ? `${current.senderName} mentioned you`
    : isNote
      ? `${current.senderName} added a note`
      : `${current.senderName} shared ${
          current.mediaKind === "image"
            ? "an image"
            : current.mediaKind === "audio"
              ? "a voice note"
              : "a file"
        }`;

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && dismissChatPopupAlert()}
    >
      <DialogContent className="sm:max-w-[440px] rounded-xl">
        <DialogHeader>
          <p className="flex items-center gap-2 text-base font-semibold text-[#051912]">
            <span
              className={`flex size-8 items-center justify-center rounded-full ${
                isMention
                  ? "bg-[#eef2ff]"
                  : isNote
                    ? "bg-[#fff4ec]"
                    : "bg-[#eaf5f1]"
              }`}
            >
              {isMention ? (
                <AtSign className="size-4 text-[#335cff]" />
              ) : isNote ? (
                <StickyNote className="size-4 text-[#ff8447]" />
              ) : (
                <MediaIcon className="size-4 text-[#156548]" />
              )}
            </span>
            {title}
          </p>
          <Separator />
          <DialogDescription
            className="pt-2 text-[13px] text-[#505e59]"
            asChild
          >
            <div>
              <p className="m-0">
                {isMention ? "In" : "On"}{" "}
                <span className="font-semibold text-[#1e302a]">
                  {current.leadName || `Lead #${current.leadId}`}
                </span>
                {isNote
                  ? current.childId
                    ? "'s deal"
                    : ""
                  : current.childId
                    ? "'s deal chat"
                    : "'s chat"}
                .
              </p>
              {current.preview ? (
                <p className="mt-2 mb-0 rounded-lg bg-[#f7f9f8] px-3 py-2 text-[12.5px] leading-[1.5] text-[#1e302a] break-words whitespace-pre-wrap">
                  {current.preview}
                </p>
              ) : null}
              {waiting > 0 ? (
                <p className="mt-2 mb-0 text-[11px] text-[#697571]">
                  +{waiting} more waiting
                </p>
              ) : null}
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={dismissChatPopupAlert}>
            {waiting > 0 ? "Next" : "Dismiss"}
          </Button>
          {isNote ? (
            <Button variant="primary" onClick={openLead}>
              Open lead
            </Button>
          ) : (
            <Button variant="primary" onClick={openChat}>
              Open chat
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
